import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {bulkUnarchiveFilesWithDataMods} from 'archive/ArchiveActions';
import {CreateAssemblyModal} from 'assembly/CreateAssemblyModal';
import {navigate, pluralize} from 'BenchlingUtil';
import {PolymerType, ResidueType} from 'biojs/BioConstants';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants, {
  LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE,
  OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE,
} from 'constants/ModelTypeConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {
  InlineBannerActionType,
  InlineBannerType,
  InlineBannerProps,
} from 'ui-library/banners/InlineBanner';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import runModal from 'ui-library/runModal';
import {typeToPluralized} from 'util/NameUtil';

interface BetaNavBannerProps {
  onClick: () => void;
}

export function getBulkAssemblyArchivedBannerProps(
  assemblyId: string,
  canUnarchive: boolean
): InlineBannerProps {
  const action = canUnarchive
    ? {
        actionType: InlineBannerActionType.CUSTOM as const,
        actionText: 'Unarchive',
        actionOnClick: () => {
          LoadingScreen.show('Unarchiving...');
          bulkUnarchiveFilesWithDataMods([assemblyId], {hasRegisteredItems: false}, () => {
            LoadingScreen.hide();
          });
        },
      }
    : undefined;
  return {
    bannerType: InlineBannerType.WARNING,
    icon: 'box-archive',
    text: 'This assembly has been archived.',
    supportingInfo: 'Editing is disabled on archived objects.',
    action,
  };
}

/**
 * A banner to display when the user has entered the redesigned bulk assembly flow from the assembly wizard,
 * enabling quick exit to the legacy tool.
 * @param props Component props
 * @param {function} props.onClick A function to call after the banner link is clicked.
 * @constructor
 */
export function AssemblyWizardBanner(props: BetaNavBannerProps): JSX.Element {
  return (
    <div className={css(styles.spacing)}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={
          <span>
            You're using the new combinatorial assembly tool. To use the Assembly Wizard,{' '}
            <a
              onClick={(e) => {
                runModal(CreateAssemblyModal, {});
                props.onClick();
              }}
            >
              click here.
            </a>
          </span>
        }
      />
    </div>
  );
}

// Props for a banner to display when the user has entered the redesigned bulk assembly flow
// from legacy assembly tools, enabling quick exit to the legacy tool, to use either as a standalone banner
// or as part of a stack of banners.
export function getLegacyBulkAssemblyBannerProps(onClick: () => void): InlineBannerProps {
  return {
    bannerType: InlineBannerType.INFO,
    text: (
      <span>
        Still using legacy bulk assembly to design constructs? If so,{' '}
        <a
          href="bulk-workflows/assembly"
          onClick={(e) => {
            navigate(e);
            onClick();
          }}
        >
          return to the old tool here
        </a>
        .
      </span>
    ),
    supportingInfo:
      'The legacy tool will remain available for a limited time. We will notify you in advance before it becomes unavailable.',
  };
}

/**
 * A banner to display when the user has entered the redesigned bulk assembly flow from legacy bulk assembly,
 * enabling quick exit to the legacy tool.
 * @param props Component props
 * @param {function} props.onClick A function to call after the banner link is clicked.
 * @constructor
 */
export function LegacyBulkAssemblyBanner(props: BetaNavBannerProps): JSX.Element | null {
  const [showBanner, setShowBanner] = useState(true);

  if (!showBanner) {
    return null;
  }
  return (
    <div className={css(styles.spacing)}>
      <InlineBanner
        {...getLegacyBulkAssemblyBannerProps(props.onClick)}
        onDismiss={() => {
          setShowBanner(false);
        }}
      />
    </div>
  );
}

interface BulkAssemblyBannerProps {
  onClick: () => void;
  actionOrigin: ActionOrigin;
}

/**
 * A dismissable banner to nudzh the user into using the redesigned cloning flow.
 * @param props Component props
 * @param props.onClick Function called when clicking the actionable link in the banner
 * @constructor
 */
export function BulkAssemblyBanner(props: BulkAssemblyBannerProps): JSX.Element | null {
  const [showBanner, setShowBanner] = useState(true);
  useEffect(() => {
    if (showBanner) {
      analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.VIEWED_BULK_ASSEMBLY_BETA_BANNER, {
        actionOrigin: props.actionOrigin,
      });
    }
  }, [props.actionOrigin, showBanner]);

  if (!showBanner) {
    return null;
  }
  return (
    <InlineBanner
      bannerType={InlineBannerType.INFO}
      text={
        <span>
          {`${
            props.actionOrigin === ActionOrigin.MOLBIO_BULK_ASSEMBLY
              ? 'This legacy tool will remain available for a limited time. '
              : ''
          } We encourage you to `}
          <a
            onClick={props.onClick}
            {...getTestProps({component: 'BulkAssemblyBanner', element: 'newAssemblyLink'})}
          >
            try the new combinatorial assembly tool here
          </a>
          .
        </span>
      }
      supportingInfo="The new combinatorial assembly tool offers a more flexible and visual experience for designing constructs."
      onDismiss={() => {
        setShowBanner(false);
      }}
    />
  );
}

/**
 * A banner with a short description of concatenation and the available output types.
 * @param props Component props
 * @param {PolymerType} props.polymerType The polymer type being concatenated
 */
export function ConcatenationBanner({polymerType}: {polymerType: PolymerType}): JSX.Element {
  let subject: string;
  if (polymerType === ResidueType.AA) {
    subject = typeToPluralized(ModelTypeConstants.PROTEIN);
  } else {
    const longSequenceName = typeToPluralized(LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[polymerType]);
    const oligoName = typeToPluralized(OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[polymerType]);
    subject = `${longSequenceName} or ${oligoName}`;
  }
  return (
    <div className={css(styles.spacing)}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`Create new ${subject} by joining multiple fragments together.`}
      />
    </div>
  );
}

export function getConstructsTabBannersProps({
  numConstructsWithErrors,
  numConstructsWithWarnings,
  isArchived,
  assemblyId,
  canUnarchive,
}: {
  numConstructsWithErrors: number;
  numConstructsWithWarnings: number;
  isArchived: boolean;
  assemblyId: string;
  canUnarchive: boolean;
}): Array<InlineBannerProps> {
  const bannerMessages: Array<InlineBannerProps> = [];
  if (isArchived) {
    bannerMessages.push(getBulkAssemblyArchivedBannerProps(assemblyId, canUnarchive));
  }
  if (numConstructsWithErrors > 0) {
    bannerMessages.push({
      bannerType: InlineBannerType.ERROR,
      text: `${numConstructsWithErrors} of your constructs ${pluralize(numConstructsWithErrors, 'is', 'are')} currently invalid.`,
      supportingInfo: (
        <span>
          These constructs cannot be assembled as currently designed. To fix the relevant errors, go to the{' '}
          <strong>Overview</strong> tab.
        </span>
      ),
      action: {actionType: InlineBannerActionType.ACCORDION},
    });
  }
  if (numConstructsWithWarnings > 0) {
    bannerMessages.push({
      bannerType: InlineBannerType.WARNING,
      text: `${numConstructsWithWarnings} of your constructs ${pluralize(numConstructsWithWarnings, 'has', 'have')} associated warnings.`,
      supportingInfo: (
        <div>
          <div>
            These warnings may cause downstream issues with your sequences, but they are not blocking errors.
            You may proceed without addressing them, if you wish.
          </div>
          <div>
            To review the warnings, go to the <strong>Overview</strong> tab.
          </div>
        </div>
      ),
      action: {actionType: InlineBannerActionType.ACCORDION},
    });
  }

  return bannerMessages;
}

const styles = StyleSheet.create({
  spacing: {
    marginBottom: '24px',
  },
});
